import { cn } from 'cn';
import { forwardRef } from 'react';

import { getIconSource, type IconMetaphor } from './constants';
import { getIcon } from './getIcon';
import { type IconGlyphProps } from './types';

export type IconProps = {
  readonly metaphor: IconMetaphor;
  readonly size?: 'sm' | 'md' | 'lg' | 'inherit';
} & Omit<IconGlyphProps, 'ref'>;

// SVG attributes and the ref reach the <svg>, so the icon can carry event
// handlers or test ids and be the direct child of an MUI Tooltip.
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { className, metaphor, size = 'md', style, ...rest },
  ref,
) {
  const IconComponent = getIcon(metaphor);

  return (
    <IconComponent
      ref={ref}
      aria-hidden
      className={cn(
        'shrink-0',
        {
          // A CSS fill overrides the glyph's own `fill="none"` and cascades into
          // stroke-only shapes, so custom glyphs keep their authored fills and strokes.
          'fill-current': getIconSource(metaphor) !== 'custom',
          // Takes the font-size of an MUI slot such as `startIcon`, like MUI SvgIcon's `fontSize="inherit"`.
          'size-[1em]': size === 'inherit',
          'size-4': size === 'sm',
          'size-6': size === 'md',
          'size-8': size === 'lg',
        },
        className,
      )}
      style={style}
      {...rest}
    />
  );
});
